import { parse } from 'cookie'
import { useCallback } from 'react'

import { useOrganizationLazyQuery, useOrganizationQuery, useUserLazyQuery } from '~generated/hooks'
import { UserDetailsFragment } from '~generated/operations'
import {
  LaunchObj,
  OnboardingGoal,
  OrganizationObj,
  RecipientDataObj,
  SurveyObj,
  SurveyRecipientObj,
} from '~generated/types'
import { ANALYTICS_EVENTS, AUTH_DATA_KEYS, BILLING_EVENTS, CLICK_EVENTS, CREATE_EVENTS } from '~lib/constants'
import useTrackUtmParams from '~lib/hooks/analytics/useTrackUtmParams'
import useCookie from '~lib/hooks/useCookie'

export interface IdentifyObj {
  id?: string
  traits: IdentifyData
}

export interface IdentifyData {
  email?: string
  name?: string
  workspace?: string
  indentifyLastStep?: string
  created?: Date
  newCompany?: string
  organizationId?: string
  newUser?: string
  company?: string
  role?: string
  companySize?: string
  companyType?: string
  launchFrequency?: string
  workspaceUsersCount?: number
  workspacePendingInvites?: number
  trialActive?: string
  isPaidUser?: string
  planName?: string
  firstUser?: string
  workspaceSlug?: string
  accountType?: string
  origin?: string
  releaseNotesStatus?: string
  source?: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
  referral?: string
  isVerified?: boolean
  intent?: OnboardingGoal
}

export interface TrackDataBase {
  userId?: string
  companyId?: string
  email?: string
}

export type SignUpData = TrackDataBase & {
  name?: string
  companyName?: string
  companyType?: string
  companySize?: string
  companyLaunches?: string
  newCompany?: string
  newUser?: string
  signupType?: string
  onboardingStarted?: string
  onboardingCompleted?: string
  lastOnboardingStep?: string
  trialActive?: string
  isPaidUser?: string
  planName?: string
  firstUser?: string
  workspaceSlug?: string
  origin?: string
  event?: string
}

export type PageData = TrackDataBase & {
  contentViewed?: string
  trialActive?: string
  planName?: string
  launchId?: string
  launchName?: string
  launchTier?: string
  sectionName?: string
  isPaidUser?: string
  companyName?: string
  companyLaunches?: string
  name?: string
  title?: string
  url?: string
  path?: string
  referrer?: string
}

export type SentUpdateData = PageData & {
  modulesSentCount?: string
  recipientsCount?: string
}

export type CopilotaDta = PageData & {
  moduleName?: string
  copilotStarted?: string
  copilotCompleted?: string
  event?: string
}

export type SubscriptionData = PageData & {
  companyName?: string
  billingEmail?: string
  previousPlanName?: string
  previousPlanId?: string
  planName?: string
  planId?: string
  frequency?: string
  updateType?: string
  event?: string
}

type CopilotSegmentData = PageData & {
  launchId: string
  launchName: string
  moduleName?: string
  completed: boolean
}

export type UserData = {
  userId?: string
  email?: string
}

type SurveyData = TrackDataBase & {
  surveyName?: string
  surveyType?: string
  status?: string
  recipients?: string
  event?: string
}

type UpdateReleaseNotesPage = TrackDataBase & {
  releaseNotesStatus: string
  title: string
}

type InviteUserData = {
  invitedUserEmail?: string
  invitedUserRole?: string
  invitedUserName?: string
}

type ClickOrCreateData = IdentifyData &
  InviteUserData & {
    userId?: string
    roadmapName?: string
    launchName?: string
    launchTier?: string
    launchType?: string
    templateType?: string
    productLine?: string
    url?: string
    moduleViewed?: string
    surveyType?: string
  }

type TrackData =
  | PageData
  | SignUpData
  | SentUpdateData
  | CopilotaDta
  | SubscriptionData
  | SurveyData
  | UpdateReleaseNotesPage
  | UserData
  | ClickOrCreateData

interface SendSurveyData extends Pick<SurveyObj, 'name' | 'status' | 'surveyType'> {
  recipients: RecipientDataObj[] | SurveyRecipientObj[]
}

const bannedEmails = ['test', 'volha', 'haveignition']

export const isAnAllowedEmail = (email: string) =>
  bannedEmails.every((bannedEmail) => !email?.toLowerCase().includes(bannedEmail))

export const useSegmentAnalyticsTrackEventBaseData = () => {
  const cookie = useCookie()
  const { data: organizationData } = useOrganizationQuery()
  const { id, name, hasPaidSubscription, isUnderFreeTrial, currentSubscriptionPlanName } =
    organizationData?.organization || {}
  const currentUserId = cookie[AUTH_DATA_KEYS.USER_ID]
  return {
    userId: currentUserId,
    companyId: id,
    companyName: name,
    trialActive: isUnderFreeTrial ? 'Y' : 'N',
    isPaidUser: hasPaidSubscription ? 'Y' : 'N',
    planName: currentSubscriptionPlanName || '',
  }
}

export const useSegmentAnalytics = () => {
  const [userQuery] = useUserLazyQuery()

  const [queryOrganization] = useOrganizationLazyQuery()

  const { utmParams } = useTrackUtmParams()

  const isAllowedToTrackDataFromLoggedUser = useCallback(
    async (userId?: string) => {
      // temporary workaround for filtering events in Segment
      // pages events are by default and they cannot be filtered
      const cookie = parse(document.cookie)
      const id = userId || cookie[AUTH_DATA_KEYS.USER_ID]
      if (!id) return true
      const user = await userQuery({
        variables: {
          id,
        },
      })
      return isAnAllowedEmail(user.data?.user?.email)
    },
    [userQuery]
  )

  const isAllowedToSendData = useCallback(
    async ({ email, userId }: UserData) => {
      if (email) return isAnAllowedEmail(email)
      return isAllowedToTrackDataFromLoggedUser(userId)
    },
    [isAllowedToTrackDataFromLoggedUser]
  )

  const identify = useCallback(
    async (id: string, traits: IdentifyData) => {
      try {
        const isAllowed = await isAllowedToSendData({ email: traits?.email })

        if (isAllowed) {
          window.analytics?.identify(id, traits)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [isAllowedToSendData]
  )

  const track = useCallback(
    async (id: string, traits: TrackData) => {
      try {
        const isAllowed = await isAllowedToSendData({ userId: traits?.userId, email: traits?.email })

        if (isAllowed) {
          window.analytics?.track(id, traits)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [isAllowedToSendData]
  )

  const page = useCallback(
    async (props: PageData, category?: string) => {
      try {
        const isAllowed = await isAllowedToSendData({})

        if (isAllowed) {
          window.analytics?.page(category, props)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [isAllowedToSendData]
  )

  const sendSignupGoogleSegmentData = useCallback(
    ({ id, name, email, isEmailVerified }: UserDetailsFragment, workspace: ANALYTICS_EVENTS) => {
      identify(id, {
        name,
        email,
        workspace,
        isVerified: isEmailVerified,
        ...utmParams,
      })
    },
    [identify, utmParams]
  )

  const sendLoginSegmentData = useCallback(
    ({ id, name, email, isEmailVerified }: UserDetailsFragment, workspace: ANALYTICS_EVENTS) =>
      identify(id, {
        name,
        email,
        workspace,
        isVerified: isEmailVerified,
      }),
    [identify]
  )

  const sendSentUpdateStatusSegmentData = useCallback(
    ({ id, name }: Pick<LaunchObj, 'id' | 'name'>, modulesSentCount: number, recipientsCount: number): void => {
      const cookie = parse(document.cookie)
      track(ANALYTICS_EVENTS.SENT_UPDATE, {
        userId: cookie[AUTH_DATA_KEYS.USER_ID],
        companyId: cookie[AUTH_DATA_KEYS.ORGANIZATION_ID],
        launchId: id,
        launchName: name,
        modulesSentCount: String(modulesSentCount),
        recipientsCount: String(recipientsCount),
      })
    },
    [track]
  )

  const sendClickOrCreateSegmentData = useCallback(
    async (eventType: CLICK_EVENTS | CREATE_EVENTS | BILLING_EVENTS, data?: ClickOrCreateData) => {
      const cookie = parse(document.cookie)
      const userId = cookie[AUTH_DATA_KEYS.USER_ID]

      const { data: { organization } = {} } = await queryOrganization()

      const {
        name,
        hasPaidSubscription,
        isUnderFreeTrial,
        currentSubscriptionPlanName,
        admin,
        users = [],
        launchesPerYear,
        slug,
      } = organization

      const firstUser = admin ? (admin === userId ? 'Y' : 'N') : ''

      const user = users?.find((user) => user.userId === userId)

      // add more fields
      track(eventType, {
        ...data,
        workspaceUsersCount: users?.filter((user) => user.inviteAccepted)?.length,
        isPaidUser: hasPaidSubscription ? 'Y' : 'N',
        trialActive: isUnderFreeTrial ? 'Y' : 'N',
        planName: currentSubscriptionPlanName || '',
        firstUser,
        company: name || '',
        name: user?.userDetails?.name || '',
        launchFrequency: launchesPerYear,
        workspaceSlug: slug || '',
        role: user?.userDetails?.jobTitle,
        accountType: user?.role || '',
      })
    },
    [queryOrganization, track]
  )

  const copilotSegmentData = useCallback(
    ({
      launchId,
      launchName,
      moduleName,
      completed,
      planName,
      trialActive,
      isPaidUser,
      companyLaunches,
      name,
    }: CopilotSegmentData): void => {
      const cookie = parse(document.cookie)
      track(ANALYTICS_EVENTS.COPILOT, {
        userId: cookie[AUTH_DATA_KEYS.USER_ID],
        companyId: cookie[AUTH_DATA_KEYS.ORGANIZATION_ID],
        launchId,
        launchName,
        moduleName: moduleName?.toLowerCase(),
        copilotStarted: 'Y',
        copilotCompleted: completed ? 'Y' : 'N',
        companyName: cookie[AUTH_DATA_KEYS.ORGANIZATION_NAME],
        planName,
        trialActive,
        isPaidUser,
        name,
        companyLaunches,
      })
    },
    [track]
  )

  const sendCopilotStartedSegmentData = useCallback(
    (data: Omit<CopilotSegmentData, 'completed'>): void => {
      copilotSegmentData({ ...data, completed: false })
    },
    [copilotSegmentData]
  )

  const sendCopilotCompletedSegmentData = useCallback(
    (data: Omit<CopilotSegmentData, 'completed'>): void => {
      copilotSegmentData({ ...data, completed: true })
    },
    [copilotSegmentData]
  )

  const sendSurveyData = useCallback(
    ({ name, status, surveyType, recipients }: SendSurveyData, organization: OrganizationObj) => {
      const cookie = parse(document.cookie)
      const firstUser = organization && (organization.admin === cookie[AUTH_DATA_KEYS.USER_ID] ? 'Y' : 'N')
      const currentUserId = cookie[AUTH_DATA_KEYS.USER_ID]
      const { isUnderFreeTrial, hasPaidSubscription, currentSubscriptionPlanName, users, launchesPerYear, slug } =
        organization || {}
      const user = users.find(({ userId }) => userId === currentUserId)
      track(ANALYTICS_EVENTS.CREATE_SURVEY, {
        userId: currentUserId,
        companyId: cookie[AUTH_DATA_KEYS.ORGANIZATION_ID],
        companyName: cookie[AUTH_DATA_KEYS.ORGANIZATION_NAME],
        companyLaunches: launchesPerYear,
        planName: currentSubscriptionPlanName || '',
        name: user?.userDetails?.name || '',
        firstUser,
        trialActive: isUnderFreeTrial ? 'Y' : 'N',
        isPaidUser: hasPaidSubscription ? 'Y' : 'N',
        workspaceSlug: slug,
        surveyName: name || '',
        surveyType: surveyType || '',
        status: status || '',
        recipients: recipients?.length.toString() || '',
      })
    },
    [track]
  )

  return {
    identify,
    track,
    page,
    sendLoginSegmentData,
    sendSentUpdateStatusSegmentData,
    sendCopilotStartedSegmentData,
    sendCopilotCompletedSegmentData,
    sendSurveyData,
    sendClickOrCreateSegmentData,
    sendSignupGoogleSegmentData,
  }
}
